/**
 * Block off dialog (DNKADM-0373 U1) — "block certain bays for certain hours", "block
 * off the whole facility", or "Ryan is out Thursday", from the reservations board, in one
 * place instead of three admin screens.
 *
 * dink-api already models this as per-target DATE EXCEPTIONS that record when a bay (or
 * coach) is OPEN on a date. This dialog is the translation layer: the operator says
 * "block", `blockOff.ts` does the inversion (the target's weekly row for that weekday
 * minus the blocked window, or the all-day closed form), and the write is one
 * `LOCATION`-scoped exception per selected target.
 *
 * **A target that is already closed that day gets NO write** (spec, corrected
 * 2026-09-05 against dink-api's `ScheduleResolver`). Four forms mean closed: no active
 * location assignment here, no weekly row for that weekday, a row marked unavailable, a
 * row available with zero shifts. Writing an exception in any of them would CREATE
 * availability where there was none — the opposite of the ask. The target is named in
 * the result instead ("Bay 3 is already closed on Mondays — nothing to block").
 *
 * Decisions carried from the spec:
 *  - **The whole facility is every bay, not the location.** A location-level hours
 *    exception does not stop a member whose access window is capped by the bay's own
 *    shifts; per-bay exceptions are airtight however the bays are configured. So
 *    "whole facility" writes the same per-bay exceptions as "certain bays", for all of
 *    them — and never a staff exception.
 *  - **Bays and coaches are separate targets**, never one mixed list.
 *  - **Existing bookings are not touched** and the dialog says so: bookings already
 *    inside the window on the selected targets are listed before the write, with a plain
 *    statement that they stay. Nothing here cancels or moves them.
 *  - **A target that already carries an exception for the date is named, not
 *    overwritten** (the server's uniqueness key is `(target, date, scope, location)`).
 *    The write proceeds for the others and the result names both sets.
 *  - **Partial failure is reported per target.** Three bays are three writes; the result
 *    panel lists what was blocked and what was not, never a blanket success.
 *  - **Remove** deletes one block (the per-row DELETE the exception panels use) and
 *    refreshes the list.
 *  - **No reason/note input**: neither exception payload carries such a field, so none
 *    is collected (spec stop condition — dropped, recorded in the delivery).
 *  - Native date/time inputs only; wall clock in the location's timezone throughout.
 */
import { useCallback, useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import type { LocationSummary } from '@/api/locations';
import type { ExceptionScope } from '@/api/resources';
import { listReservations, type Reservation } from '@/api/reservations';
import {
  createResourceDateException,
  deleteResourceDateException,
  getResourceLocations,
  getResourceSchedule,
  listResourceDateExceptions,
  listResources,
  updateResourceDateException,
} from '@/api/resources';
import {
  createStaffDateException,
  deleteStaffDateException,
  getStaffLocations,
  getStaffSchedule,
  listStaff,
  listStaffDateExceptions,
  staffDisplayName,
  updateStaffDateException,
} from '@/api/staff';
import { serverMessage } from '@/api/serverMessage';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import type { ShiftLike } from '@/utils/shiftValidation';
import { deriveStatusBucket, localDateValueOf, localHourOf } from './calendar/derive';
import {
  ALREADY_BLOCKED_MESSAGE,
  clockLabel,
  closeOf,
  dayOfWeekOf,
  effectiveShiftsFor,
  openHoursSentence,
  parseBlockRange,
  planBlock,
  reservationsInWindow,
  WEEKDAY_PLURALS,
  type BlockPlan,
  type BlockTargetKind,
  type MinuteRange,
} from './blockOff';

type TargetChoice = 'facility' | 'bays' | 'coaches';

interface Target {
  kind: BlockTargetKind;
  id: string;
  name: string;
  /**
   * Whether the roster row said it is assigned HERE: `true` when its inline `locations`
   * named this location, `null` when the field was absent (unknown — checked with the
   * per-target assignment read before any write). A row assigned only elsewhere is
   * never listed.
   */
  assignedHere: true | null;
}

interface Targets {
  bays: Target[];
  coaches: Target[];
}

/** One block already on the selected date for a target at this location. */
export interface ExistingBlock {
  target: Target;
  /** The server row id (present on reads; used to DELETE). */
  exceptionId: string | null;
  isAvailable: boolean;
  shifts: ShiftLike[];
  /**
   * The row's own scope (DNKADM-0388 U3): an update in place must send it back as it is —
   * an `ALL`-scoped exception narrowed to `LOCATION` would silently reopen every other
   * location to the weekly row.
   */
  scope: ExceptionScope;
  locationId: string | null;
}

/**
 * What one target's day looks like BEFORE any write (DNKADM-0388 U3): whether it is assigned
 * here, and its base shifts (the weekly row for that weekday, `[]` = closed). `error` carries a
 * failed read for that one target — it fails alone, never the whole dialog.
 */
interface TargetDay {
  assignedHere: boolean;
  base: ShiftLike[];
  error?: string;
}

interface DayState {
  existing: ExistingBlock[];
  reservations: Reservation[];
  /** Keyed by `${kind}:${id}`. */
  days: Map<string, TargetDay>;
}

function targetKey(target: Pick<Target, 'kind' | 'id'>): string {
  return `${target.kind}:${target.id}`;
}

interface Outcome {
  target: Target;
  /** `blocked` = written; `closed` = already closed that day, nothing written; `failed` = not written. */
  status: 'blocked' | 'closed' | 'failed';
  /** For `closed` / `failed`: the reason, phrased to follow the target's name. */
  message?: string;
}

export interface BlockOffDialogProps {
  open: boolean;
  /** The board's active location — every write is scoped `LOCATION` to it. */
  locationId: string;
  /** The location's IANA zone (fail-soft null) — reads the day's bookings on the venue clock. */
  timeZone: string | null;
  /** The date the board is showing — the dialog's default. */
  initialDate: string;
  onClose: () => void;
  /** Something was written or removed — the board should re-read its blocks. */
  onChanged: () => void;
}

/**
 * A roster row's assignment here from its inline `locations` (DNKADM-0078): `true` when
 * it names this location, `null` when the field is absent (unknown — resolved by the
 * per-target assignment read at write time), `false` when it names only other locations
 * (never listed).
 */
function assignedHere(
  locations: LocationSummary[] | undefined,
  locationId: string,
): true | false | null {
  if (locations === undefined) return null;
  return locations.some((l) => l.id === locationId);
}

async function loadTargets(locationId: string): Promise<Targets> {
  const [resources, staff] = await Promise.all([
    listResources({ status: 'active' }),
    listStaff({ status: 'active' }),
  ]);
  const bays: Target[] = [];
  for (const r of resources) {
    const here = assignedHere(r.locations, locationId);
    if (here === false) continue;
    bays.push({ kind: 'bay', id: r.id, name: r.name.trim() || r.id, assignedHere: here });
  }
  const coaches: Target[] = [];
  for (const s of staff) {
    const here = assignedHere(s.locations, locationId);
    if (here === false) continue;
    coaches.push({ kind: 'coach', id: s.id, name: staffDisplayName(s), assignedHere: here });
  }
  return { bays, coaches };
}

/** The four closed forms, decided per target BEFORE any write (never a location fallback). */
async function isAssignedHere(target: Target, locationId: string): Promise<boolean> {
  if (target.assignedHere === true) return true;
  const ids =
    target.kind === 'bay'
      ? await getResourceLocations(target.id)
      : await getStaffLocations(target.id);
  return ids.includes(locationId);
}

async function loadExisting(
  targets: Target[],
  date: string,
  locationId: string,
): Promise<ExistingBlock[]> {
  const perTarget = await Promise.all(
    targets.map(async (target) => {
      const rows =
        target.kind === 'bay'
          ? await listResourceDateExceptions(target.id)
          : await listStaffDateExceptions(target.id);
      return rows
        .filter(
          (e) =>
            e.exceptionDate === date && (e.scope === 'ALL' || e.locationId === locationId),
        )
        .map<ExistingBlock>((e) => ({
          target,
          exceptionId: e.id,
          isAvailable: e.isAvailable,
          shifts: e.shifts,
          scope: e.scope,
          locationId: e.locationId,
        }));
    }),
  );
  return perTarget.flat();
}

/**
 * Each target's assignment-here + base shifts for `date` (DNKADM-0388 U3) — read once per
 * date so the preview and the write decide from the SAME data. Closed form 1 (not assigned
 * here) is decided before any schedule read; a failed read is recorded on that target only.
 */
async function loadTargetDays(
  targets: Target[],
  date: string,
  locationId: string,
): Promise<Map<string, TargetDay>> {
  const dow = dayOfWeekOf(date);
  const entries = await Promise.all(
    targets.map(async (target): Promise<[string, TargetDay]> => {
      try {
        if (!(await isAssignedHere(target, locationId))) {
          return [targetKey(target), { assignedHere: false, base: [] }];
        }
        const schedule =
          target.kind === 'bay'
            ? await getResourceSchedule(target.id, locationId)
            : await getStaffSchedule(target.id, locationId);
        const row = dow === null ? undefined : schedule.days.find((d) => d.dayOfWeek === dow);
        return [targetKey(target), { assignedHere: true, base: effectiveShiftsFor(row) }];
      } catch (err) {
        return [
          targetKey(target),
          { assignedHere: true, base: [], error: serverMessage(err) ?? 'its hours couldn’t be read' },
        ];
      }
    }),
  );
  return new Map(entries);
}

/**
 * The location's live bookings on `date` (venue clock). The fetch window is a generous
 * ±1 day of UTC instants around the calendar date, then narrowed client-side by the
 * venue-local date — no naive-time round trip.
 */
async function loadReservations(
  locationId: string,
  date: string,
  timeZone: string | null,
): Promise<Reservation[]> {
  const anchor = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(anchor.getTime())) return [];
  const start = new Date(anchor);
  start.setUTCDate(start.getUTCDate() - 1);
  const end = new Date(anchor);
  end.setUTCDate(end.getUTCDate() + 2);
  const rows = await listReservations({
    locationId,
    start: start.toISOString(),
    end: end.toISOString(),
  });
  return rows.filter(
    (r) =>
      localDateValueOf(r.startTime, timeZone) === date &&
      deriveStatusBucket(r.status) !== 'cancelled',
  );
}

function timeLabel(iso: string | null, timeZone: string | null): string {
  const hour = localHourOf(iso, timeZone);
  if (hour === null) return '—';
  const h = Math.floor(hour);
  const min = Math.round((hour - h) * 60);
  return clockLabel(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
}

function blockLabel(block: ExistingBlock): string {
  if (!block.isAvailable) return 'All day';
  return `Open ${block.shifts.map((s) => `${clockLabel(s.startTime)}–${clockLabel(s.endTime)}`).join(', ')}`;
}

function kindNoun(kind: BlockTargetKind, count: number): string {
  if (kind === 'bay') return count === 1 ? 'bay' : 'bays';
  return count === 1 ? 'coach' : 'coaches';
}

export function BlockOffDialog({
  open,
  locationId,
  timeZone,
  initialDate,
  onClose,
  onChanged,
}: BlockOffDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const ids = useId();
  const showToast = useToast();

  const [date, setDate] = useState(initialDate);
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [choice, setChoice] = useState<TargetChoice>('facility');
  const [selectedBays, setSelectedBays] = useState<ReadonlySet<string>>(new Set());
  const [selectedCoaches, setSelectedCoaches] = useState<ReadonlySet<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[] | null>(null);
  // Remove all (DNKADM-0388 U2): the confirmation step, the in-flight flag, and the
  // per-block failures of the last run (null = none / not run).
  const [confirmingRemoveAll, setConfirmingRemoveAll] = useState(false);
  const [removingAll, setRemovingAll] = useState(false);
  const [removeFailures, setRemoveFailures] = useState<
    { key: string; name: string; message: string }[] | null
  >(null);

  const targets = useResource(() => loadTargets(locationId), [locationId]);
  const allTargets = useMemo<Target[]>(
    () => (targets.data ? [...targets.data.bays, ...targets.data.coaches] : []),
    [targets.data],
  );
  const dayState = useResource<DayState | null>(async () => {
    if (!targets.data) return null;
    const [existing, reservations, days] = await Promise.all([
      loadExisting(allTargets, date, locationId),
      loadReservations(locationId, date, timeZone),
      loadTargetDays(allTargets, date, locationId),
    ]);
    return { existing, reservations, days };
  }, [allTargets, date, locationId, timeZone]);

  const kind: BlockTargetKind = choice === 'coaches' ? 'coach' : 'bay';
  const chosenTargets = useMemo<Target[]>(() => {
    if (!targets.data) return [];
    if (choice === 'facility') return targets.data.bays;
    if (choice === 'bays') return targets.data.bays.filter((b) => selectedBays.has(b.id));
    return targets.data.coaches.filter((c) => selectedCoaches.has(c.id));
  }, [targets.data, choice, selectedBays, selectedCoaches]);

  const parsedRange = allDay ? null : parseBlockRange(startTime, endTime);
  const blocked: MinuteRange | null = typeof parsedRange === 'string' ? null : parsedRange;
  const rangeUsable = allDay || typeof parsedRange !== 'string';

  const affected = useMemo(() => {
    if (!dayState.data || !rangeUsable) return [];
    return reservationsInWindow(
      dayState.data.reservations,
      kind,
      new Set(chosenTargets.map((c) => c.id)),
      blocked,
      timeZone,
    );
  }, [dayState.data, rangeUsable, kind, chosenTargets, blocked, timeZone]);

  const existing = useMemo(() => dayState.data?.existing ?? [], [dayState.data]);
  const loading = targets.status === 'loading' || dayState.status === 'loading';

  // The per-target decision (DNKADM-0388 U3), made from the day's loaded data and shared
  // by the preview and the write — what the staff member sees is what will be saved.
  type Decision =
    | { status: 'closed'; message: string }
    | { status: 'failed'; message: string }
    | {
        status: 'write';
        plan: Extract<BlockPlan, { kind: 'create' | 'update' }>;
        base: ShiftLike[];
        /** The row an `update` writes back to — its scope is preserved verbatim. */
        current?: ExistingBlock;
      };
  const decide = useCallback(
    (target: Target): Decision => {
      const day = dayState.data?.days.get(targetKey(target));
      const dow = dayOfWeekOf(date);
      const weekday = dow === null ? 'that day' : WEEKDAY_PLURALS[dow];
      if (!day) return { status: 'failed', message: 'the block couldn’t be written' };
      if (day.error) return { status: 'failed', message: day.error };
      if (!day.assignedHere) {
        return { status: 'closed', message: 'is not assigned to this location — nothing to block' };
      }
      const matches = existing.filter((b) => targetKey(b.target) === targetKey(target));
      // Two rows on the date (an ALL and a LOCATION one): which to update is not ours to
      // guess — the pre-0388 refusal stands.
      if (matches.length > 1) return { status: 'failed', message: ALREADY_BLOCKED_MESSAGE };
      const current = matches[0];
      const plan = planBlock(day.base, current, blocked);
      if (plan.kind === 'closed-already') {
        return { status: 'closed', message: `is already closed on ${weekday} — nothing to block` };
      }
      if (plan.kind === 'refused') return { status: 'failed', message: plan.message };
      return current ? { status: 'write', plan, base: day.base, current } : { status: 'write', plan, base: day.base };
    },
    [dayState.data, existing, date, blocked],
  );
  const preview = useMemo(
    () =>
      dayState.data && rangeUsable
        ? chosenTargets.map((target) => {
            const d = decide(target);
            const text =
              d.status === 'write'
                ? openHoursSentence(target.name, d.plan.write, closeOf(d.base))
                : `${target.name} ${d.message}`;
            return { key: targetKey(target), text };
          })
        : [],
    [dayState.data, rangeUsable, chosenTargets, decide],
  );
  const loadFailed = targets.status === 'error' || dayState.status === 'error';

  function toggle(setter: (next: ReadonlySet<string>) => void, current: ReadonlySet<string>, id: string) {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  }

  async function handleBlock() {
    setFormError(null);
    setOutcomes(null);
    if (date.trim() === '') {
      setFormError('Choose a date.');
      return;
    }
    if (!allDay && typeof parsedRange === 'string') {
      setFormError(parsedRange);
      return;
    }
    if (chosenTargets.length === 0) {
      setFormError(
        choice === 'facility'
          ? 'There are no bays at this location to block.'
          : `Choose at least one ${kindNoun(kind, 1)} to block.`,
      );
      return;
    }
    if (!dayState.data) {
      setFormError('The day’s existing blocks couldn’t be read, so nothing was written. Try again.');
      return;
    }

    setSaving(true);
    const results: Outcome[] = [];
    try {
      for (const target of chosenTargets) {
        // The same decision the preview showed (DNKADM-0388 U3): closed forms 1–4 name the
        // target and write nothing; a closed / all-day / id-less existing block is refused; an
        // open existing block is updated in place with the new window subtracted from ITS hours.
        const decision = decide(target);
        if (decision.status !== 'write') {
          results.push({ target, status: decision.status, message: decision.message });
          continue;
        }
        try {
          const { plan } = decision;
          if (plan.kind === 'update') {
            // Updated IN PLACE: the row keeps its own scope. Narrowing an `ALL` row to this
            // location would reopen every other location to the weekly row.
            const current = decision.current;
            const input = {
              exceptionDate: date,
              isAvailable: plan.write.isAvailable,
              scope: current?.scope ?? ('LOCATION' as const),
              ...(current?.scope === 'ALL'
                ? {}
                : { locationId: current?.locationId ?? locationId }),
              shifts: plan.write.shifts,
            };
            if (target.kind === 'bay') {
              await updateResourceDateException(target.id, plan.exceptionId, input);
            } else {
              await updateStaffDateException(target.id, plan.exceptionId, input);
            }
            results.push({ target, status: 'blocked' });
            continue;
          }
          const input = {
            exceptionDate: date,
            isAvailable: plan.write.isAvailable,
            scope: 'LOCATION' as const,
            locationId,
            shifts: plan.write.shifts,
          };
          if (target.kind === 'bay') {
            await createResourceDateException(target.id, input);
          } else {
            await createStaffDateException(target.id, input);
          }
          results.push({ target, status: 'blocked' });
        } catch (err) {
          results.push({
            target,
            status: 'failed',
            message: serverMessage(err) ?? 'the block couldn’t be written',
          });
        }
      }
    } finally {
      setSaving(false);
    }

    setOutcomes(results);
    const blockedCount = results.filter((r) => r.status === 'blocked').length;
    const failedCount = results.filter((r) => r.status === 'failed').length;
    if (blockedCount > 0) {
      dayState.refetch();
      onChanged();
      if (failedCount === 0) {
        showToast(`Blocked ${blockedCount} ${kindNoun(kind, blockedCount)}`, 'success');
      }
      if (choice === 'bays') setSelectedBays(new Set());
      if (choice === 'coaches') setSelectedCoaches(new Set());
    }
  }

  async function handleRemove(block: ExistingBlock) {
    if (block.exceptionId === null) return;
    const key = `${block.target.kind}:${block.target.id}:${block.exceptionId}`;
    setRemovingKey(key);
    try {
      if (block.target.kind === 'bay') {
        await deleteResourceDateException(block.target.id, block.exceptionId);
      } else {
        await deleteStaffDateException(block.target.id, block.exceptionId);
      }
      setOutcomes(null);
      dayState.refetch();
      onChanged();
      showToast(`Removed the block on ${block.target.name}`, 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? 'The block couldn’t be removed.', 'error');
    } finally {
      setRemovingKey(null);
    }
  }

  /**
   * Remove every block on the date (DNKADM-0388 U2): one DELETE per block, in order, every
   * failure named. Success is reported only when ALL of them went — a partial removal is
   * a warning with the survivors listed, never a success toast.
   */
  async function handleRemoveAll() {
    const blocks = existing.filter((b) => b.exceptionId !== null);
    setConfirmingRemoveAll(false);
    setRemoveFailures(null);
    setOutcomes(null);
    setRemovingAll(true);
    const failures: { key: string; name: string; message: string }[] = [];
    let removed = 0;
    try {
      for (const block of blocks) {
        if (block.exceptionId === null) continue;
        try {
          if (block.target.kind === 'bay') {
            await deleteResourceDateException(block.target.id, block.exceptionId);
          } else {
            await deleteStaffDateException(block.target.id, block.exceptionId);
          }
          removed += 1;
        } catch (err) {
          failures.push({
            key: `${targetKey(block.target)}:${block.exceptionId}`,
            name: block.target.name,
            message: serverMessage(err) ?? 'the block couldn’t be removed',
          });
        }
      }
    } finally {
      setRemovingAll(false);
    }
    if (removed > 0) {
      dayState.refetch();
      onChanged();
    }
    if (failures.length === 0) {
      showToast(`Removed ${removed} ${removed === 1 ? 'block' : 'blocks'}`, 'success');
    } else {
      setRemoveFailures(failures);
      showToast(
        `${failures.length} of ${blocks.length} blocks couldn’t be removed`,
        'error',
      );
    }
  }

  const busy = saving || loading || removingAll;

  return (
    <Dialog
      open={open}
      onClose={saving || removingAll ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      aria-labelledby={`${ids}-title`}
    >
      <DialogTitle id={`${ids}-title`}>Block off</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* When */}
          <Stack spacing={1.5}>
            <TextField
              type="date"
              label="Date"
              size="small"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setOutcomes(null);
              }}
              disabled={saving}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ maxWidth: 220 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={allDay}
                  onChange={(e) => setAllDay(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'All day' } }}
                />
              }
              label="All day"
            />
            <Stack direction="row" sx={{ gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                type="time"
                label="From"
                size="small"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={saving || allDay}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'aria-label': 'Block start time' },
                }}
                sx={{ width: 150 }}
              />
              <TextField
                type="time"
                label="To"
                size="small"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={saving || allDay}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'aria-label': 'Block end time' },
                }}
                sx={{ width: 150 }}
              />
            </Stack>
          </Stack>

          {/* What */}
          <FormControl component="fieldset" disabled={saving}>
            <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              What to block
            </FormLabel>
            <RadioGroup
              value={choice}
              onChange={(e) => {
                setChoice(e.target.value as TargetChoice);
                setOutcomes(null);
              }}
            >
              <FormControlLabel
                value="facility"
                control={<Radio />}
                label="The whole facility (every bay)"
              />
              <FormControlLabel value="bays" control={<Radio />} label="Certain bays" />
              <FormControlLabel value="coaches" control={<Radio />} label="A coach" />
            </RadioGroup>
          </FormControl>

          {targets.status === 'loading' && (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }} role="status">
              <CircularProgress size={16} />
              <Typography variant="body2">Loading bays and coaches…</Typography>
            </Stack>
          )}
          {targets.status === 'error' && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={targets.refetch}>
                  Retry
                </Button>
              }
            >
              The bays and coaches couldn’t be loaded.
            </Alert>
          )}

          {targets.data && choice === 'facility' && (
            <Typography variant="body2" sx={{ color: t.text2 }}>
              {targets.data.bays.length === 0
                ? 'No bays are assigned to this location.'
                : `Blocks every bay here: ${targets.data.bays.map((b) => b.name).join(', ')}.`}
            </Typography>
          )}
          {targets.data && choice === 'bays' && (
            <FormGroup aria-label="Bays" sx={{ pl: 0.5 }}>
              {targets.data.bays.length === 0 && (
                <Typography variant="body2" sx={{ color: t.text2 }}>
                  No bays are assigned to this location.
                </Typography>
              )}
              {targets.data.bays.map((bay) => (
                <FormControlLabel
                  key={bay.id}
                  control={
                    <Checkbox
                      checked={selectedBays.has(bay.id)}
                      onChange={() => toggle(setSelectedBays, selectedBays, bay.id)}
                      disabled={saving}
                    />
                  }
                  label={bay.name}
                />
              ))}
            </FormGroup>
          )}
          {targets.data && choice === 'coaches' && (
            <FormGroup aria-label="Coaches" sx={{ pl: 0.5 }}>
              {targets.data.coaches.length === 0 && (
                <Typography variant="body2" sx={{ color: t.text2 }}>
                  No coaches are assigned to this location.
                </Typography>
              )}
              {targets.data.coaches.map((coach) => (
                <FormControlLabel
                  key={coach.id}
                  control={
                    <Checkbox
                      checked={selectedCoaches.has(coach.id)}
                      onChange={() => toggle(setSelectedCoaches, selectedCoaches, coach.id)}
                      disabled={saving}
                    />
                  }
                  label={coach.name}
                />
              ))}
            </FormGroup>
          )}

          {/* Existing bookings inside the window — they stay. */}
          {dayState.status === 'error' && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={dayState.refetch}>
                  Retry
                </Button>
              }
            >
              The day’s bookings and blocks couldn’t be loaded. Nothing can be blocked until
              they are.
            </Alert>
          )}
          {dayState.data && chosenTargets.length > 0 && rangeUsable && (
            <Box data-testid="block-off-affected">
              <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                Already booked in this window
              </Typography>
              {affected.length === 0 ? (
                <Typography variant="body2" sx={{ color: t.text2 }}>
                  Nothing is booked on the selected {kindNoun(kind, chosenTargets.length)} in this
                  window.
                </Typography>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    These bookings stay. A block only stops new bookings — it does not cancel or
                    move anything already on the books. Move or cancel them separately.
                  </Alert>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5, gap: 0.25 }}>
                    {affected.map((r) => (
                      <Typography
                        component="li"
                        variant="body2"
                        key={r.reservationUuid ?? `${r.startTime}-${r.contactName}`}
                      >
                        {timeLabel(r.startTime, timeZone)}–{timeLabel(r.endTime, timeZone)} ·{' '}
                        {r.contactName ?? 'Booking'} ·{' '}
                        {kind === 'bay'
                          ? (r.assignedResourceName ?? 'Bay')
                          : (r.assignedStaffName ?? 'Coach')}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          )}

          {/* What will be saved (DNKADM-0388 U3) — the resulting hours per target, from the
              same decision the write uses, so the staff member sees it before saving. */}
          {preview.length > 0 && (
            <Box data-testid="block-off-preview">
              <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                What will be saved
              </Typography>
              <Stack component="ul" sx={{ m: 0, pl: 2.5, gap: 0.25 }}>
                {preview.map((line) => (
                  <Typography component="li" variant="body2" key={line.key}>
                    {line.text}.
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {formError && (
            <Typography role="alert" sx={{ fontSize: 13, color: 'error.main', fontWeight: 500 }}>
              {formError}
            </Typography>
          )}

          {outcomes && (
            <Alert
              severity={
                outcomes.some((o) => o.status === 'failed')
                  ? 'warning'
                  : outcomes.some((o) => o.status === 'blocked')
                    ? 'success'
                    : 'info'
              }
              data-testid="block-off-result"
            >
              {outcomes.some((o) => o.status === 'blocked') && (
                <Typography variant="body2">
                  Blocked:{' '}
                  {outcomes
                    .filter((o) => o.status === 'blocked')
                    .map((o) => o.target.name)
                    .join(', ')}
                  .
                </Typography>
              )}
              {outcomes
                .filter((o) => o.status === 'closed')
                .map((o) => (
                  <Typography variant="body2" key={`${o.target.kind}:${o.target.id}`}>
                    {o.target.name} {o.message}.
                  </Typography>
                ))}
              {outcomes
                .filter((o) => o.status === 'failed')
                .map((o) => (
                  <Typography variant="body2" key={`${o.target.kind}:${o.target.id}`}>
                    Not blocked — {o.target.name} {o.message}.
                  </Typography>
                ))}
            </Alert>
          )}

          {/* Blocks already on this date, with Remove. */}
          {dayState.data && (
            <Box data-testid="block-off-existing">
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Blocks on this date</Typography>
                {existing.length > 0 && !confirmingRemoveAll && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      setRemoveFailures(null);
                      setConfirmingRemoveAll(true);
                    }}
                    disabled={saving || removingAll || removingKey !== null}
                  >
                    Remove all blocks on this date
                  </Button>
                )}
              </Stack>
              {removeFailures && (
                <Alert severity="warning" sx={{ mb: 1 }} data-testid="block-off-remove-failures">
                  {removeFailures.map((f) => (
                    <Typography variant="body2" key={f.key}>
                      Not removed — {f.name}: {f.message}.
                    </Typography>
                  ))}
                </Alert>
              )}
              {confirmingRemoveAll && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1 }}
                  data-testid="block-off-remove-all-confirm"
                  action={
                    <Stack direction="row" sx={{ gap: 0.5 }}>
                      <Button color="inherit" size="small" onClick={() => setConfirmingRemoveAll(false)}>
                        Keep them
                      </Button>
                      <Button color="inherit" size="small" variant="outlined" onClick={() => void handleRemoveAll()}>
                        Remove all
                      </Button>
                    </Stack>
                  }
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Remove {existing.length} {existing.length === 1 ? 'block' : 'blocks'} on this date?
                  </Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {existing.map((block) => (
                      <Typography component="li" variant="body2" key={`${targetKey(block.target)}:${block.exceptionId ?? ''}`}>
                        {block.target.name} · {blockLabel(block)}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              )}
              {existing.length === 0 ? (
                <Typography variant="body2" sx={{ color: t.text2 }}>
                  No blocks on this date.
                </Typography>
              ) : (
                <Stack component="ul" sx={{ m: 0, p: 0, listStyle: 'none', gap: 0.5 }}>
                  {existing.map((block) => {
                    const key = `${block.target.kind}:${block.target.id}:${block.exceptionId ?? ''}`;
                    return (
                      <Stack
                        component="li"
                        key={key}
                        direction="row"
                        sx={{ alignItems: 'center', gap: 1, minHeight: 44 }}
                      >
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                          <strong>{block.target.name}</strong> · {blockLabel(block)}
                        </Typography>
                        <IconAction
                          size="small"
                          label={`Remove block on ${block.target.name}`}
                          onClick={() => void handleRemove(block)}
                          disabled={block.exceptionId === null || removingKey === key || saving || removingAll}
                        >
                          <Icon name="delete" sx={{ fontSize: 20 }} />
                        </IconAction>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || removingAll}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleBlock()}
          disabled={busy || loadFailed || !targets.data}
        >
          {saving ? 'Blocking…' : 'Block off'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
